import React, { useCallback, useState } from 'react';
import ExploreSongsTable, { ExploreSongsSortableKeys } from '../../components/ExploreSongsTable/ExploreSongsTable';
import { OrderingValues, TrndsClientSongTiktokStats } from '@round/api';
import useUrlState from '../../../../Hooks/useUrlState';
import styles from './ExploreClientSongs.module.css';
import Select from '../../../../ui/DataEntry/Select/Select';
import SearchInput from '../../../../ui/DataEntry/SearchInput/SearchInput';
import { GenericDropdownOption } from '../../../../App.types';
import { StylesConfig } from 'react-select';
import ModalRight from '../../../../SharedComponents/ModalRight/ModalRight';
import SongDetails from './SongDetails/SongDetails';
import { ReactComponent as CloseIcon } from '../../../../assets/Close.svg';
import { ClientSongCollectionsProvider } from '../AddClientSongToCollection/ClientSongCollectionsContext';
import { Button } from '@round/ui-kit';
import CreateClientSongModal from '../CreateClientSongModal/CreateClientSongModal';
import { showNotification } from '../../../../helpers';
import useClientSongs from './useClientSongs';

type UrlState = Partial<{
    page: number;
    page_size: number;
    search: string;
    ordering: OrderingValues<ExploreSongsSortableKeys>;
    songId: number;
}>;

type OrderingOption = GenericDropdownOption<OrderingValues<ExploreSongsSortableKeys>>;
const orderingOptions: OrderingOption[] = [
    { label: 'Total Videos', value: '-total_video_count' },
    { label: 'Daily Growth (# videos)', value: '-total_video_count_daily_change' },
    { label: 'Daily Growth (% uplift)', value: '-total_video_count_daily_change_relative' },
    { label: 'Weekly Growth (# videos)', value: '-total_video_count_weekly_change' },
    { label: 'Weekly Growth (% uplift)', value: '-total_video_count_weekly_change_relative' },
];

const sortingSelectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#C2CFE0',
    }),
    valueContainer: (base) => ({
        ...base,
        minWidth: '10rem',
    }),
    singleValue: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
    menuList: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
};

type ExploreClientSongsProps = { clientId: number };

const ExploreClientSongs = ({ clientId }: ExploreClientSongsProps) => {
    const [isAddSongModalOpen, setIsAddSongModalOpen] = useState(false);
    const [urlState, setUrlState] = useUrlState<UrlState>({
        page: 1,
        page_size: 10,
        search: '',
        ordering: '-total_video_count_daily_change',
    });

    const [selectedSongStats, setSelectedSongStats] = useState<TrndsClientSongTiktokStats | null>(null);
    const songDetailsModalSongId = Number(urlState.songId);
    const isSongDetailsModalOpen = !isNaN(songDetailsModalSongId);

    const { songStats, count, isLoading, hasErrorLoading, reset, refresh, isInitialized } = useClientSongs({
        ordering: urlState.ordering as OrderingValues<ExploreSongsSortableKeys>,
        search: urlState.search,
        page: Number(urlState.page) || 1,
        page_size: Number(urlState.page_size) || 10,
        clientId: clientId,
    });

    const closeModal = () => {
        setSelectedSongStats(null);
        setUrlState({ songId: undefined });
    };

    const onSongAdded = useCallback(() => {
        setIsAddSongModalOpen(false);
        showNotification('Successfully added song', 'info');
        refresh();
    }, [refresh]);

    return (
        <>
            <div className={styles.container}>
                <h1 className={styles.title}>Songs</h1>

                <div className={styles.toolbar}>
                    <SearchInput
                        className={styles.search}
                        value={urlState.search}
                        onChange={(search) => {
                            setUrlState({ search });
                            reset();
                        }}
                    />

                    <div className={styles.toolbarActions}>
                        <div className={styles.toolbarItem}>
                            <label className={styles.toolbarItemLabel}>Sort by</label>
                            <Select
                                isSearchable={false}
                                className={styles.orderingSelect}
                                menuPortalTarget={document.body}
                                styles={sortingSelectStyles}
                                options={orderingOptions}
                                value={orderingOptions.find((option) => option.value === urlState.ordering)}
                                onChange={(ordering) => setUrlState({ ordering: ordering?.value })}
                            />
                        </div>
                        <Button type="filled" color="black" onClick={() => setIsAddSongModalOpen(true)}>
                            Add new
                        </Button>
                    </div>
                </div>

                <ClientSongCollectionsProvider>
                    <ExploreSongsTable
                        clientId={clientId}
                        loading={isLoading || (!isInitialized && !hasErrorLoading)}
                        ordering={urlState.ordering as OrderingValues<ExploreSongsSortableKeys>}
                        data={songStats}
                        count={count}
                        page={Number(urlState.page)}
                        setPage={(page) => setUrlState({ page })}
                        pageSize={Number(urlState.page_size)}
                        setPageSize={(pageSize) => setUrlState({ page_size: pageSize || 10 })}
                        onRowClick={(row) => {
                            setSelectedSongStats(row);
                            setUrlState({ songId: row.song.id });
                        }}
                        noDataLabel={
                            hasErrorLoading ? 'Sorry, we are experiencing technical issues' : 'No data available'
                        }
                    />

                    <ModalRight isModalOpen={isSongDetailsModalOpen} closeModal={closeModal} className={styles.modal}>
                        <div className={styles.modalContent}>
                            <button className={styles.modalCloseButton} onClick={closeModal}>
                                <CloseIcon width={16} height={16} />
                            </button>
                            {!isNaN(Number(urlState.songId)) && (
                                <SongDetails
                                    key={urlState.songId}
                                    songId={Number(urlState.songId)}
                                    clientId={clientId}
                                    songStats={selectedSongStats}
                                />
                            )}
                        </div>
                    </ModalRight>
                </ClientSongCollectionsProvider>
            </div>

            <CreateClientSongModal
                clientId={clientId}
                isModalOpen={isAddSongModalOpen}
                onCreated={onSongAdded}
                closeModal={() => setIsAddSongModalOpen(false)}
            />
        </>
    );
};

export default ExploreClientSongs;
